import React, { useCallback, useEffect, useRef, useState } from "react"
import ApexCharts, { ApexOptions } from 'apexcharts'
import { DateTime } from 'luxon'
import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils"
import { APIError } from "../../app/modules/auth"
import { getDashboardGraphData } from "../core/_requests"

interface DateRange {
  label: string
  getValue: () => { start: number; end: number }
  isCustom?: boolean
}

const dateRangesByGrouping = {
  day: [
    {
      label: 'Last 7 Days',
      getValue: () => ({
        start: Math.floor(DateTime.local().setZone('America/Chicago').minus({ days: 7 }).startOf('day').toSeconds()),
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('day').toSeconds())
      })
    },
    {
      label: 'Last 30 Days',
      getValue: () => ({
        start: Math.floor(DateTime.local().setZone('America/Chicago').minus({ days: 30 }).startOf('day').toSeconds()),
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('day').toSeconds())
      })
    },
    {
      label: 'Last 3 Months',
      getValue: () => ({
        start: Math.floor(DateTime.local().setZone('America/Chicago').minus({ months: 3 }).startOf('day').toSeconds()),
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('day').toSeconds())
      })
    },
    {
      label: 'Last 6 Months',
      getValue: () => ({
        start: Math.floor(DateTime.local().setZone('America/Chicago').minus({ months: 6 }).startOf('day').toSeconds()),
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('day').toSeconds())
      })
    },
    {
      label: 'Custom Dates',
      getValue: () => ({ start: 0, end: 0 }),
      isCustom: true
    }
  ],
  month: [
    {
      label: 'Last 3 Months',
      getValue: () => ({
        // Current month + 2 previous complete months (e.g., July, August, September for Sept 16)
        start: Math.floor(DateTime.local().setZone('America/Chicago').startOf('month').minus({ months: 2 }).toSeconds()),
        // End at the last moment of the current month
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('month').toSeconds())
      })
    },
    {
      label: 'Last 6 Months',
      getValue: () => ({
        // Current month + 5 previous complete months
        start: Math.floor(DateTime.local().setZone('America/Chicago').startOf('month').minus({ months: 5 }).toSeconds()),
        // End at the last moment of the current month
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('month').toSeconds())
      })
    },
    {
      label: 'Last 12 Months',
      getValue: () => ({
        // Current month + 11 previous complete months
        start: Math.floor(DateTime.local().setZone('America/Chicago').startOf('month').minus({ months: 11 }).toSeconds()),
        // End at the last moment of the current month
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('month').toSeconds())
      })
    },
    {
      label: 'Year to Date',
      getValue: () => ({
        start: Math.floor(DateTime.local().setZone('America/Chicago').startOf('year').toSeconds()),
        // End at the last moment of the current month
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('month').toSeconds())
      })
    },
    {
      label: 'Custom Dates',
      getValue: () => ({ start: 0, end: 0 }),
      isCustom: true
    }
  ],
  year: [
    {
      label: 'Last 2 Years',
      getValue: () => ({
        start: Math.floor(DateTime.local().setZone('America/Chicago').minus({ years: 2 }).startOf('year').toSeconds()),
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('day').toSeconds())
      })
    },
    {
      label: 'Last 3 Years',
      getValue: () => ({
        start: Math.floor(DateTime.local().setZone('America/Chicago').minus({ years: 3 }).startOf('year').toSeconds()),
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('day').toSeconds())
      })
    },
    {
      label: 'Last 5 Years',
      getValue: () => ({
        start: Math.floor(DateTime.local().setZone('America/Chicago').minus({ years: 5 }).startOf('year').toSeconds()),
        end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('day').toSeconds())
      })
    },
    {
      label: 'Custom Dates',
      getValue: () => ({ start: 0, end: 0 }),
      isCustom: true
    }
  ]
}

interface LeadSource {
  key: string
  label: string
  color: string
  enabled: boolean
}

type GroupBy = 'day' | 'month' | 'year'

export default function DashboardLeadsFocused() {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartInstance = useRef<ApexCharts | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingError, setLoadingError] = useState<string | null>(null)
  const [selectedRange, setSelectedRange] = useState<string>('Last 7 Days')
  const [customDateRange, setCustomDateRange] = useState(false)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [groupBy, setGroupBy] = useState<GroupBy>('day')
  const [dashboardData, setDashboardData] = useState<any>(null)

  const [leadSources, setLeadSources] = useState<LeadSource[]>([
    { key: 'event_admins', label: 'Event Admins', color: '#0173B2', enabled: true }, // Blue - universally distinguishable
    { key: 'check_ins', label: 'Check-ins', color: '#DE8F05', enabled: true }, // Orange - safe for all colorblind types
    { key: 'direct_mail', label: 'Direct Mail', color: '#CC78BC', enabled: true }, // Purple - good contrast
    { key: 'passare', label: 'Passare', color: '#029E73', enabled: true } // Teal green - distinct from blue
  ])

  const toggleLeadSource = (key: string) => {
    setLeadSources(prev => prev.map(source =>
      source.key === key ? { ...source, enabled: !source.enabled } : source
    ))
  }

  // Get the current date ranges based on grouping
  const getCurrentDateRanges = () => dateRangesByGrouping[groupBy as keyof typeof dateRangesByGrouping] || dateRangesByGrouping.day

  // Check if current settings differ from defaults
  const isNonDefaultSettings = () => {
    const defaultGroupBy = 'day'
    const defaultRange = 'Last 7 Days'
    const allSourcesEnabled = leadSources.every(s => s.enabled)

    return groupBy !== defaultGroupBy ||
           selectedRange !== defaultRange ||
           customDateRange ||
           !allSourcesEnabled
  }

  // Reset to default settings
  const resetToDefaults = async () => {
    // Reset all settings to defaults
    setGroupBy('day')
    setSelectedRange('Last 7 Days')
    setCustomDateRange(false)
    setStartDate('')
    setEndDate('')
    // Reset all lead sources to enabled
    setLeadSources(prev => prev.map(source => ({ ...source, enabled: true })))

    // Load data with default settings immediately
    // We calculate the default date range here to ensure immediate refresh
    const defaultRange = {
      start: Math.floor(DateTime.local().setZone('America/Chicago').minus({ days: 7 }).startOf('day').toSeconds()),
      end: Math.floor(DateTime.local().setZone('America/Chicago').endOf('day').toSeconds())
    }

    setIsLoading(true)
    try {
      const data = await getDashboardGraphData(defaultRange.start, defaultRange.end, null, 'day')
      setDashboardData(data)

      // Update chart with fresh data
      if (chartRef.current) {
        const options = getChartOptions(data, 'month')
        if (chartInstance.current) {
          chartInstance.current.updateOptions(options, false, true)
        } else {
          chartInstance.current = new ApexCharts(chartRef.current, options)
          chartInstance.current.render()
        }
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err)
      setLoadingError(err instanceof APIError ? err.message : 'Failed to load dashboard data')
    } finally {
      setIsLoading(false)
    }
  }

  const getDateRange = useCallback(() => {
    // Use groupBy ref to avoid dependency
    const currentGroupBy = groupBy
    const currentRanges = dateRangesByGrouping[currentGroupBy as keyof typeof dateRangesByGrouping] || dateRangesByGrouping.day
    const range = currentRanges.find(r => r.label === selectedRange)
    if (range?.isCustom && startDate && endDate) {
      return {
        start: Math.floor(DateTime.fromISO(startDate, { zone: 'America/Chicago' }).startOf('day').toSeconds()),
        end: Math.floor(DateTime.fromISO(endDate, { zone: 'America/Chicago' }).endOf('day').toSeconds())
      }
    }
    return range ? range.getValue() : currentRanges[0].getValue() // Default to first option
  }, [selectedRange, startDate, endDate]) // Don't include groupBy to prevent reload

  const loadDashboardData = useCallback(async (shouldAnimate = true) => {
    setIsLoading(true)
    try {
      const { start, end } = getDateRange()

      // Always fetch day-level data; we'll aggregate client-side based on groupBy
      const data = await getDashboardGraphData(start, end, null, 'day')
      console.log('Dashboard API response:', data)
      console.log('Passare specific data:', {
        total_passare_leads: data?.total_passare_leads,
        passare_leads_by_day: data?.passare_leads_by_day,
        passare_array_length: data?.passare_leads_by_day?.length
      })
      setDashboardData(data)

      // Update chart
      if (chartRef.current) {
        const options = getChartOptions(data, groupBy)

        if (chartInstance.current) {
          // Update existing chart without destroying it
          chartInstance.current.updateOptions(options, false, shouldAnimate)
        } else {
          // Create new chart only if it doesn't exist
          const chart = new ApexCharts(chartRef.current, options)
          chartInstance.current = chart
          chart.render()
        }
      }

      setLoadingError(null)
    } catch (error: any) {
      console.error('Dashboard load error:', error)
      const errorMessage = error instanceof APIError ? error.message : 'Failed to load dashboard data'
      setLoadingError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [getDateRange]) // Remove groupBy to prevent auto-refresh when grouping changes

  useEffect(() => {
    // Update chart when sources are toggled
    if (chartRef.current && dashboardData) {
      const options = getChartOptions(dashboardData, groupBy)
      if (chartInstance.current) {
        chartInstance.current.updateOptions(options, false, false)
      }
    }
  }, [leadSources, dashboardData]) // Removed groupBy to prevent auto-update

  // When groupBy changes, reset the selected range and update chart display
  useEffect(() => {
    const currentRanges = getCurrentDateRanges()
    // Reset to first option if current selection doesn't exist in new grouping
    if (!currentRanges.find(r => r.label === selectedRange)) {
      setSelectedRange(currentRanges[0].label)
      setCustomDateRange(false)
    }
  }, [groupBy, selectedRange])

  // This effect is no longer needed since we don't want auto-updates on groupBy change
  // Users must explicitly refresh to see changes

  useEffect(() => {
    // Initial load
    loadDashboardData(true)
  }, [])

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }
    }
  }, [])

  // Helper function to aggregate data by month or year
  const aggregateData = (dayData: any[], groupBy: string) => {
    if (!dayData || groupBy === 'day') return dayData

    const grouped: { [key: string]: number } = {}

    dayData.forEach((item: any) => {
      const dateStr = item.x || item.full_date
      if (!dateStr) return

      // Parse date - it already has T00:00:00Z appended
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return // Skip invalid dates

      let key: string

      if (groupBy === 'month') {
        // Group by first day of month - use noon UTC to avoid timezone issues
        const year = date.getUTCFullYear()
        const month = date.getUTCMonth() + 1
        key = `${year}-${String(month).padStart(2, '0')}-01T12:00:00Z`
      } else if (groupBy === 'year') {
        // Group by first day of year - use noon UTC to avoid timezone issues
        key = `${date.getUTCFullYear()}-01-01T12:00:00Z`
      } else {
        // Keep the date at noon UTC for day grouping
        key = date.toISOString().split('T')[0] + 'T12:00:00Z'
      }

      const value = item.y !== undefined ? item.y : 0
      grouped[key] = (grouped[key] || 0) + value
    })

    const result = Object.entries(grouped)
      .map(([date, value]) => ({ x: date, y: value }))
      // Don't filter out zeros - we want to show days with no activity
      .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime())

    // Debug logging
    if (groupBy !== 'day') {
      console.log(`Aggregated data for ${groupBy}:`, result)
      console.log(`Input data length: ${dayData.length}, Output data length: ${result.length}`)
      console.log(`Grouped keys:`, Object.keys(grouped))
      console.log(`Result x values:`, result.map(r => r.x))
      console.log(`Result y values:`, result.map(r => r.y))
      console.log(`Input data sample:`, dayData.slice(0, 3))
    }

    return result
  }

  const getChartOptions = (data: any, currentGroupBy: string = 'day'): ApexOptions => {
    const labelColor = getCSSVariableValue('--kt-gray-500')
    const borderColor = getCSSVariableValue('--kt-gray-200')

    const enabledSources = leadSources.filter(s => s.enabled)
    const series: any[] = []

    // Map the data we have to series
    if (data) {
      if (enabledSources.some(s => s.key === 'event_admins') && data.event_administrators_by_day) {
        const rawData = data.event_administrators_by_day.map((d: any) => ({
          // Use T12:00:00Z (noon UTC) to avoid date shifting issues at midnight
          x: d.full_date + 'T12:00:00Z',
          y: d.unique_administrators || 0
        }))
        const total = data.total_event_administrators || 0

        // Date debugging for Event Admins (comparison)
        console.log('Event Admins date analysis:', {
          firstFive: data.event_administrators_by_day.slice(0, 5).map((d: any) => ({
            full_date: d.full_date,
            isoString: new Date(d.full_date).toISOString()
          })),
          lastFive: data.event_administrators_by_day.slice(-5).map((d: any) => ({
            full_date: d.full_date,
            isoString: new Date(d.full_date).toISOString()
          }))
        })

        series.push({
          name: `Event Admins (${total})`,
          data: aggregateData(rawData, currentGroupBy)
        })
      }
      if (enabledSources.some(s => s.key === 'check_ins') && data.event_check_in_records_by_day) {
        const rawData = data.event_check_in_records_by_day.map((d: any) => ({
          // Use T12:00:00Z (noon UTC) to avoid date shifting issues at midnight
          x: d.full_date + 'T12:00:00Z',
          y: d.unique_check_in_records || 0
        }))
        const total = data.total_event_check_in_records || 0
        series.push({
          name: `Check-ins (${total})`,
          data: aggregateData(rawData, currentGroupBy)
        })
      }
      if (enabledSources.some(s => s.key === 'direct_mail') && data.direct_mail_replies_by_day) {
        const rawData = data.direct_mail_replies_by_day.map((d: any) => ({
          // Use T12:00:00Z (noon UTC) to avoid date shifting issues at midnight
          x: d.full_date + 'T12:00:00Z',
          y: d.unique_direct_mail_replies || 0
        }))
        const total = data.total_direct_mail_replies || 0
        series.push({
          name: `Direct Mail (${total})`,
          data: aggregateData(rawData, currentGroupBy)
        })
      }
      if (enabledSources.some(s => s.key === 'passare') && data.passare_leads_by_day) {
        const rawData = data.passare_leads_by_day.map((d: any) => ({
          // Use T12:00:00Z (noon UTC) to avoid date shifting issues at midnight
          x: d.full_date + 'T12:00:00Z',
          y: d.unique_passare_acquaintances || 0
        }))
        const total = data.total_passare_leads || 0

        // Detailed date debugging for Passare with range info
        console.log('Passare date analysis for', selectedRange, ':', {
          selectedRange,
          apiDataLength: data.passare_leads_by_day.length,
          total: total,
          rawDates: data.passare_leads_by_day.slice(0, 5).map((d: any) => ({
            full_date: d.full_date,
            value: d.unique_passare_acquaintances,
            withTZ: d.full_date + 'T12:00:00Z',
            parsed: new Date(d.full_date + 'T12:00:00Z'),
            isoString: new Date(d.full_date + 'T12:00:00Z').toISOString(),
            localString: new Date(d.full_date + 'T12:00:00Z').toLocaleDateString()
          })),
          lastFiveDates: data.passare_leads_by_day.slice(-5).map((d: any) => ({
            full_date: d.full_date,
            value: d.unique_passare_acquaintances,
            withTZ: d.full_date + 'T12:00:00Z',
            parsed: new Date(d.full_date + 'T12:00:00Z'),
            isoString: new Date(d.full_date + 'T12:00:00Z').toISOString(),
            localString: new Date(d.full_date + 'T12:00:00Z').toLocaleDateString()
          }))
        })

        console.log('Passare series data:', {
          enabled: enabledSources.some(s => s.key === 'passare'),
          hasData: !!data.passare_leads_by_day,
          dataLength: data.passare_leads_by_day?.length,
          total: total,
          rawDataSample: rawData.slice(0, 3),
          aggregatedSample: aggregateData(rawData, currentGroupBy).slice(0, 3)
        })
        const aggregatedData = aggregateData(rawData, currentGroupBy)

        // Log the aggregated Passare data to see what's actually displayed
        console.log('Passare aggregated data:', {
          groupBy: currentGroupBy,
          dataPoints: aggregatedData.length,
          firstThree: aggregatedData.slice(0, 3).map(d => ({
            x: d.x,
            xParsed: new Date(d.x),
            xLocal: new Date(d.x).toLocaleDateString(),
            y: d.y
          })),
          lastThree: aggregatedData.slice(-3).map(d => ({
            x: d.x,
            xParsed: new Date(d.x),
            xLocal: new Date(d.x).toLocaleDateString(),
            y: d.y
          }))
        })

        series.push({
          name: `Passare (${total})`,
          data: aggregatedData
        })
      } else {
        console.log('Passare series NOT added:', {
          enabled: enabledSources.some(s => s.key === 'passare'),
          hasData: !!data.passare_leads_by_day,
          dataExists: !!data
        })
      }
    }

    // Debug chart options for non-day groupings
    if (currentGroupBy !== 'day') {
      const allDates = series.flatMap(s => s.data.map((d: any) => new Date(d.x).getTime())).filter(Boolean)
      const uniqueDates = Array.from(new Set(allDates)).sort()

      console.log(`Chart options for ${currentGroupBy}:`, {
        seriesCount: series.length,
        totalDataPoints: allDates.length,
        uniqueDateCount: uniqueDates.length,
        xAxisConfig: {
          min: allDates.length > 0 ? Math.min(...allDates) : undefined,
          max: allDates.length > 0 ? Math.max(...allDates) : undefined
        },
        seriesData: series.map(s => ({
          name: s.name,
          dataPoints: s.data.length,
          xValues: s.data.map((d: any) => d.x),
          actualDates: s.data.map((d: any) => new Date(d.x).toISOString())
        })),
        uniqueDateTimestamps: uniqueDates,
        uniqueDateStrings: uniqueDates.map(ts => new Date(ts).toISOString())
      })
    }

    return {
      series,
      chart: {
        type: 'area',
        height: '100%',
        toolbar: { show: false },
        zoom: { enabled: false },
        sparkline: { enabled: false },
        stacked: true,
        events: {
          // Ensure tooltip shows all series on hover
          mounted: function(chart: any) {
            chart.windowResizeHandler()
          }
        }
      },
      dataLabels: { enabled: false },
      markers: {
        size: 0, // Hidden by default
        hover: {
          size: 6, // Show on hover
          sizeOffset: 3
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      fill: {
        type: 'gradient',
        gradient: {
          opacityFrom: 0.5,
          opacityTo: 0.1,
        }
      },
      xaxis: {
        type: 'datetime',
        crosshairs: {
          show: true,
          width: 1,
          position: 'back',
          opacity: 0.9,
          stroke: {
            color: '#b6b6b6',
            width: 1,
            dashArray: 3
          }
        },
        labels: {
          style: {
            colors: labelColor,
            fontSize: '12px',
          },
          datetimeUTC: true,  // Force UTC parsing to avoid timezone shifts
          datetimeFormatter: {
            year: 'yyyy',
            month: currentGroupBy === 'year' ? 'yyyy' : (currentGroupBy === 'month' ? "MMM yyyy" : "MMM 'yy"),
            day: currentGroupBy === 'year' ? 'yyyy' : (currentGroupBy === 'month' ? 'MMM yyyy' : 'dd MMM'),
            hour: 'HH:mm'
          }
        },
        // For month grouping, set tickAmount to control label generation
        ...(currentGroupBy === 'month' && {
          tickAmount: 'dataPoints',
          tickPlacement: 'on'
        }),
        // Set min/max based on actual data range across all series
        ...(series.length > 0 && (() => {
          const allDates = series.flatMap(s => s.data.map((d: any) => new Date(d.x).getTime())).filter(Boolean)
          if (allDates.length > 0) {
            return {
              min: Math.min(...allDates),
              max: Math.max(...allDates)
            }
          }
          return {}
        })()),
      },
      yaxis: {
        labels: {
          style: {
            colors: labelColor,
            fontSize: '12px',
          },
          formatter: (val) => val.toFixed(0)
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        labels: { colors: labelColor },
        itemMargin: { horizontal: 10 }
      },
      tooltip: {
        enabled: true,
        followCursor: true, // Follow the mouse cursor
        shared: true, // Show all series values for the hovered x-axis point
        intersect: false, // Trigger on x-axis hover, not just data points
        style: {
          fontSize: '12px',
          fontFamily: 'inherit'
        },
        theme: 'dark', // Dark theme for better readability
        custom: function({ series: activeSeries, seriesIndex, dataPointIndex, w }) {
          // Custom tooltip to show totals
          if (dataPointIndex < 0) return ''

          let tooltipHtml = '<div class="apexcharts-tooltip-title" style="font-family: inherit; font-size: 12px; padding: 6px 10px; background: rgba(0,0,0,0.7); border-bottom: 1px solid rgba(255,255,255,0.1);">'

          // Format the date - using the category value which should align with x-axis
          const categories = w.globals.categoryLabels || w.globals.labels
          const xValue = w.globals.seriesX[0][dataPointIndex]

          // Debug logging
          console.log('Tooltip debug:', {
            dataPointIndex,
            xValue,
            xValueDate: new Date(xValue).toISOString(),
            xValueUTC: new Date(xValue).toUTCString(),
            categories: categories?.[dataPointIndex],
            seriesX: w.globals.seriesX[0]?.[dataPointIndex],
            labels: w.globals.labels?.[dataPointIndex],
            categoryLabels: w.globals.categoryLabels?.[dataPointIndex],
            allSeriesData: w.globals.series.map((s: any, idx: number) => ({
              name: w.globals.seriesNames[idx],
              dataPoint: dataPointIndex < s.length ? s[dataPointIndex] : null,
              xValue: w.globals.seriesX[idx]?.[dataPointIndex]
            }))
          })

          let dateStr = ''
          // Use UTC methods to avoid timezone shifts
          if (currentGroupBy === 'month') {
            const date = new Date(xValue)
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                              'July', 'August', 'September', 'October', 'November', 'December']
            dateStr = `${monthNames[date.getUTCMonth()]} ${date.getUTCFullYear()}`
          } else if (currentGroupBy === 'year') {
            const date = new Date(xValue)
            dateStr = date.getUTCFullYear().toString()
          } else {
            const date = new Date(xValue)
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            dateStr = `${monthNames[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`
          }
          tooltipHtml += dateStr + '</div>'

          // Add series values
          tooltipHtml += '<div style="padding: 10px;">'
          let total = 0

          w.globals.seriesNames.forEach((name: string, idx: number) => {
            const value = activeSeries[idx] ? activeSeries[idx][dataPointIndex] : 0
            if (value !== undefined && value !== null) {
              total += value
              const color = w.globals.colors[idx]
              tooltipHtml += `
                <div style="display: flex; align-items: center; margin-bottom: 4px;">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>
                  <span style="flex: 1; color: #fff;">${name}:</span>
                  <span style="font-weight: 600; color: #fff; margin-left: 8px;">${value}</span>
                </div>
              `
            }
          })

          // Add total
          tooltipHtml += `
            <div style="border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 8px;">
              <div style="display: flex; align-items: center; font-weight: bold;">
                <span style="flex: 1; color: #fff;">Total:</span>
                <span style="color: #fff; margin-left: 8px;">${total} leads</span>
              </div>
            </div>
          `

          tooltipHtml += '</div>'
          return tooltipHtml
        }
      },
      colors: enabledSources.map(s => s.color),
      grid: {
        borderColor: borderColor,
        strokeDashArray: 4,
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        },
      },
    }
  }

  const getTotalLeads = () => {
    if (!dashboardData) return 0
    return (dashboardData.total_events || 0) +
           (dashboardData.total_event_administrators || 0) +
           (dashboardData.total_event_check_in_records || 0) +
           (dashboardData.total_direct_mail_replies || 0)
  }

  // Calculate percentage change based on comparing first half vs second half of date range
  const getPercentageChange = (sourceKey: string): number | null => {
    if (!dashboardData) return null

    let dayData: any[] = []
    switch(sourceKey) {
      case 'events': dayData = dashboardData.events_by_day || []; break
      case 'event_admins': dayData = dashboardData.event_administrators_by_day || []; break
      case 'check_ins': dayData = dashboardData.event_check_in_records_by_day || []; break
      case 'direct_mail': dayData = dashboardData.direct_mail_replies_by_day || []; break
      default: return null
    }

    if (dayData.length < 2) return null

    const midpoint = Math.floor(dayData.length / 2)
    const firstHalf = dayData.slice(0, midpoint)
    const secondHalf = dayData.slice(midpoint)

    const getSum = (data: any[], field: string) =>
      data.reduce((sum, item) => sum + (item[field] || 0), 0)

    let firstSum = 0, secondSum = 0
    switch(sourceKey) {
      case 'events':
        firstSum = getSum(firstHalf, 'unique_events')
        secondSum = getSum(secondHalf, 'unique_events')
        break
      case 'event_admins':
        firstSum = getSum(firstHalf, 'unique_administrators')
        secondSum = getSum(secondHalf, 'unique_administrators')
        break
      case 'check_ins':
        firstSum = getSum(firstHalf, 'unique_check_in_records')
        secondSum = getSum(secondHalf, 'unique_check_in_records')
        break
      case 'direct_mail':
        firstSum = getSum(firstHalf, 'unique_direct_mail_replies')
        secondSum = getSum(secondHalf, 'unique_direct_mail_replies')
        break
    }

    if (firstSum === 0) return secondSum > 0 ? 100 : 0
    return Math.round(((secondSum - firstSum) / firstSum) * 100)
  }

  return (
    <div className="card h-100 d-flex flex-column">
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">Lead Activity</span>
          <span className="text-muted fw-semibold fs-7">
            Total: {getTotalLeads().toLocaleString()} leads
          </span>
        </h3>

        <div className="card-toolbar">
          <div className="d-flex align-items-center gap-2">
            {/* Reset button - only show when settings differ from defaults */}
            {isNonDefaultSettings() && (
              <button
                className="btn btn-sm btn-link text-muted me-2"
                onClick={resetToDefaults}
                title="Reset to default view"
                style={{ fontSize: '12px', textDecoration: 'none' }}
              >
                Reset
              </button>
            )}

            {/* Filter Dropdown */}
            <div className="dropdown me-2">
              <button
                type="button"
                className="btn btn-sm btn-light position-relative"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="indicator-label">
                  Filter
                </span>
                {leadSources.some(s => !s.enabled) && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: '0.7em', padding: '0.3em 0.5em' }}
                    title="Filters applied"
                  >
                    ●
                  </span>
                )}
              </button>
              <div className="dropdown-menu p-3" style={{ minWidth: '300px' }}>
                <h6 className="dropdown-header">Lead Sources</h6>
                {leadSources.map(source => {
                  let count = 0
                  if (dashboardData) {
                    switch(source.key) {
                      case 'event_admins': count = dashboardData.total_event_administrators || 0; break
                      case 'check_ins': count = dashboardData.total_event_check_in_records || 0; break
                      case 'direct_mail': count = dashboardData.total_direct_mail_replies || 0; break
                      case 'passare': count = dashboardData.total_passare_leads || 0; break
                    }
                  }

                  const percentChange = getPercentageChange(source.key)

                  return (
                    <div key={source.key} className="form-check d-flex align-items-center mb-2">
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        id={`source-${source.key}`}
                        checked={source.enabled}
                        onChange={() => toggleLeadSource(source.key)}
                      />
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <label
                          className="form-check-label d-flex align-items-center cursor-pointer"
                          htmlFor={`source-${source.key}`}
                        >
                          <span
                            className="badge badge-circle me-2"
                            style={{
                              backgroundColor: source.color,
                              width: '8px',
                              height: '8px',
                              padding: 0,
                              opacity: source.enabled ? 1 : 0.3
                            }}
                          />
                          <span style={{ opacity: source.enabled ? 1 : 0.6 }}>
                            {source.label}: {count.toLocaleString()}
                          </span>
                        </label>
                        {percentChange !== null && (
                          <span
                            className={`badge badge-sm ${
                              percentChange >= 0 ? 'badge-light-success' : 'badge-light-danger'
                            }`}
                            style={{ fontSize: '10px', opacity: source.enabled ? 1 : 0.6 }}
                          >
                            {percentChange >= 0 ? '+' : ''}{percentChange}%
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Group By Selector */}
            <select
              className="form-select form-select-sm"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              style={{ width: 'auto' }}
            >
              <option value="day">By Day</option>
              <option value="month">By Month</option>
              <option value="year">By Year</option>
            </select>

            {/* Date Range Selector */}
            <select
              className="form-select form-select-sm"
              value={selectedRange}
              onChange={(e) => {
                const value = e.target.value
                setSelectedRange(value)
                setCustomDateRange(value === 'Custom Dates')
              }}
              style={{ minWidth: '140px' }}
            >
              {getCurrentDateRanges().map(range => (
                <option key={range.label} value={range.label}>{range.label}</option>
              ))}
            </select>

            {/* Custom Date Inputs */}
            {customDateRange && (
              <>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: 'auto' }}
                />
                <span className="text-muted">to</span>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: 'auto' }}
                />
              </>
            )}

            <button
              className="btn btn-sm btn-primary"
              onClick={() => loadDashboardData(true)}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm"></span>
              ) : (
                <span>Refresh</span>
              )}
            </button>

          </div>
        </div>
      </div>

      <div className="card-body flex-grow-1 d-flex flex-column">

        {/* Chart */}
        {loadingError ? (
          <div className="alert alert-danger">
            <div className="alert-text">{loadingError}</div>
          </div>
        ) : (
          <div ref={chartRef} className="flex-grow-1" style={{ minHeight: '400px' }}></div>
        )}
      </div>
    </div>
  )
}